import {
  AMPERSAND_CHAR_CODE,
  CHAR_OF_INTEREST_REGEX,
  CONTIGUOUS_STRING_SPACE_CHAR,
  DOUBLE_QUOTE_CHAR_CODE,
  EQUAL_SIGN_CHAR_CODE,
  FORWARD_SLASH_CHAR_CODE,
  MATCH_ALL_LINE_BREAKS,
  MATCH_LINE_BREAKS,
  NEWLINE_CHAR_CODE,
  SEMICOLON_CHAR_CODE,
  SIMPLIFY_WHITESPACE_REGEX,
  SINGLE_QUOTE_CHAR_CODE,
  SPACES_BEFORE_AND_END,
  TAG_CLOSE_CHAR_CODE,
  TAG_OPEN_CHAR_CODE,
  TRIM_TRAILING_SPACES,
  VOID_ELEMENTS,
} from '@/utils/Inputs/htmlTagsAndRegex';

function indexOfWhiteSpace(string, fromIndex) {
  const { length } = string;
  for (let i = fromIndex; i < length; i++) {
    if (isWhiteSpace(string.charCodeAt(i))) {
      return i;
    }
  }
  return length;
}

function isCharacterReferenceCharacter(charCode) {
  return (
    (charCode >= 48 && charCode <= 57) ||
    (charCode >= 65 && charCode <= 90) ||
    (charCode >= 97 && charCode <= 122)
  );
}

function isWhiteSpace(charCode) {
  return (
    charCode === 9 ||
    charCode === 10 ||
    charCode === 12 ||
    charCode === 13 ||
    charCode === 32
  );
}

// for  tags not displaying their whitespace-only content
function shouldSimplifyWhiteSpace(tagStack) {
  for (let i = tagStack.length - 1; i >= 0; i--) {
    const tagName = tagStack[i];
    if (tagName === 'li' || tagName === 'td') {
      return false;
    }
    if (tagName === 'ol' || tagName === 'table' || tagName === 'ul') {
      return true;
    }
  }
  return false;
}

function simplifyWhiteSpace(string) {
  return string.trim().replace(SIMPLIFY_WHITESPACE_REGEX, ' ');
}

function takeHtmlCharAt(string, index) {
  let char = String.fromCharCode(string.charCodeAt(index));
  if (char === '&') {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      index++;
      const nextCharCode = string.charCodeAt(index);
      if (isCharacterReferenceCharacter(nextCharCode)) {
        char += String.fromCharCode(nextCharCode);
      } else if (nextCharCode === SEMICOLON_CHAR_CODE) {
        char += String.fromCharCode(nextCharCode);
        break;
      } else {
        break;
      }
    }
  }
  return char;
}

export const charCountForHtmlContent = string => {
  let textString = new DOMParser().parseFromString(string, 'text/html').body
    .textContent;
  return trimAdditionalSpacesForCounting(textString).length;
};

export const convertPlainTextToHtml = text => {
  if (text.indexOf('<div>') > -1) {
    return text;
  }

  // replace double newlines with <br> tag
  text = text
    .replace(TRIM_TRAILING_SPACES, '')
    .replaceAll('\n\n', '<div><br></div>');
  let customItems = text.split(MATCH_LINE_BREAKS);

  for (let i = 0; i < customItems.length; i++) {
    customItems[i] = '<div>' + customItems[i] + '</div>';
  }
  customItems = customItems.join('');

  return customItems;
};

export const trimAdditionalSpacesForCounting = textString => {
  // check for a single multi-line string
  if (textString.length > 0 && textString.indexOf('\n') < 0) {
    textString = textString.replace(CONTIGUOUS_STRING_SPACE_CHAR, ' ').trim();
  }
  return textString
    .replace(SPACES_BEFORE_AND_END, '')
    .replace(MATCH_ALL_LINE_BREAKS, '');
};

export const clipHtml = (string, maxLength) => {
  let numChars = 0;

  let i = 0;
  // Stack of currently open HTML tags.
  const tagStack = [];
  const { length } = string;
  for (; i < length; i++) {
    const rest = i ? string.slice(i) : string;
    const nextIndex = rest.search(CHAR_OF_INTEREST_REGEX);
    const nextBlockSize = nextIndex > -1 ? nextIndex : rest.length;
    i += nextBlockSize;

    if (shouldSimplifyWhiteSpace(tagStack)) {
      numChars += simplifyWhiteSpace(
        nextBlockSize === rest.length ? rest : rest.slice(0, nextIndex)
      ).length;
      if (numChars > maxLength) {
        i -= nextBlockSize; // We just cut off the entire incorrectly placed text...
        break;
      }
    } else {
      numChars += nextBlockSize;
      if (numChars > maxLength) {
        i = Math.max(i - numChars + maxLength, 0);
        break;
      }
    }

    if (nextIndex === -1) {
      break;
    }

    const charCode = string.charCodeAt(i);
    if (charCode === TAG_OPEN_CHAR_CODE) {
      // don't open new tags if we are currently at the limit
      if (
        numChars === maxLength &&
        string.charCodeAt(i + 1) !== FORWARD_SLASH_CHAR_CODE
      ) {
        numChars++;
        break;
      }

      let attributeQuoteCharCode = 0;
      let endIndex = i;
      let isAttributeValue = false;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        endIndex++;

        const charCode = string.charCodeAt(endIndex);
        if (isAttributeValue) {
          if (attributeQuoteCharCode) {
            if (charCode === attributeQuoteCharCode) {
              isAttributeValue = false;
            }
          } else {
            if (isWhiteSpace(charCode)) {
              isAttributeValue = false;
            } else if (charCode === TAG_CLOSE_CHAR_CODE) {
              isAttributeValue = false;
              // re-evaluate this character
              endIndex--;
            }
          }
        } else if (charCode === EQUAL_SIGN_CHAR_CODE) {
          while (isWhiteSpace(string.charCodeAt(endIndex + 1))) {
            endIndex++; // skip whitespace
          }
          isAttributeValue = true;

          const firstAttributeCharCode = string.charCodeAt(endIndex + 1);
          if (
            firstAttributeCharCode === DOUBLE_QUOTE_CHAR_CODE ||
            firstAttributeCharCode === SINGLE_QUOTE_CHAR_CODE
          ) {
            attributeQuoteCharCode = firstAttributeCharCode;
            endIndex++;
          } else {
            attributeQuoteCharCode = 0;
          }
        } else if (charCode === TAG_CLOSE_CHAR_CODE) {
          const isEndTag = string.charCodeAt(i + 1) === FORWARD_SLASH_CHAR_CODE;
          const tagNameStartIndex = i + (isEndTag ? 2 : 1);
          const tagNameEndIndex = Math.min(
            indexOfWhiteSpace(string, tagNameStartIndex),
            endIndex
          );
          let tagName = string
            .slice(tagNameStartIndex, tagNameEndIndex)
            .toLowerCase();
          if (
            tagName.charCodeAt(tagName.length - 1) === FORWARD_SLASH_CHAR_CODE
          ) {
            // Remove trailing slash for self-closing tag names like <br/>
            tagName = tagName.slice(0, tagName.length - 1);
          }

          if (
            !(
              VOID_ELEMENTS.includes(tagName) ||
              string.charCodeAt(endIndex - 1) === FORWARD_SLASH_CHAR_CODE
            )
          ) {
            tagStack.push(tagName);
          }
          i = endIndex;
          break;
        }
      }
      if (numChars > maxLength) {
        break;
      }
    } else if (charCode === AMPERSAND_CHAR_CODE) {
      let endIndex = i + 1;
      let isCharacterReference = true;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const charCode = string.charCodeAt(endIndex);
        if (isCharacterReferenceCharacter(charCode)) {
          endIndex++;
        } else if (charCode === SEMICOLON_CHAR_CODE) {
          break;
        } else {
          isCharacterReference = false;
          break;
        }
      }

      numChars++;
      if (numChars > maxLength) {
        break;
      }

      if (isCharacterReference) {
        i = endIndex;
      }
    } else if (charCode === NEWLINE_CHAR_CODE) {
      if (!shouldSimplifyWhiteSpace(tagStack)) {
        numChars++;
        if (numChars > maxLength) {
          break;
        }
      }
    } else {
      numChars++;
      if (numChars > maxLength) {
        break;
      }
    }
  }

  if (numChars > maxLength) {
    let nextChar = takeHtmlCharAt(string, i);
    //closing tags
    while (
      nextChar === '<' &&
      string.charCodeAt(i + 1) === FORWARD_SLASH_CHAR_CODE
    ) {
      const tagName = tagStack.pop();
      const tagEndIndex = tagName ? string.indexOf('>', i + 2) : -1;
      if (
        tagEndIndex === -1 ||
        string.slice(i + 2, tagEndIndex).trim() !== tagName
      )
        i = tagEndIndex + 1;
      nextChar = string.charAt(i);
    }

    if (i < string.length) {
      let result = string.slice(0, i);
      while (tagStack.length) {
        const tagName = tagStack.pop();
        result += `</${tagName}>`;
      }
      return result;
    }
  }

  return string;
};
